import React, { useState, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Helper functions for gig type
function getGigTypeLabel(gig) {
  const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
  switch (gigType) {
    case 'sub_for_other':
      return '🔄 Substituting for Someone';
    case 'sub_for_me':
      return '👥 Someone Substituting for Me';
    default:
      return '👤 My Gig';
  }
}

function getGigTypeClass(gig) {
  const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
  switch (gigType) {
    case 'sub_for_other':
      return 'substitution-status';
    case 'sub_for_me':
      return 'sub-for-me-status';
    default:
      return 'mine-status';
  }
}

function GigDetails({ gig, onEdit, onDelete, onAddExpense, onEditExpense, onExpenseDelete, onAddAdditionalCharge, onEditAdditionalCharge, onAdditionalChargeDelete, onGigUpdate }) {
  const [isCanceled, setIsCanceled] = useState(gig.is_canceled === 1 || gig.is_canceled === true);
  const [isPaid, setIsPaid] = useState(gig.paid === 1 || gig.paid === true);
  const [substituteNotPaid, setSubstituteNotPaid] = useState(gig.substitute_not_paid === 1 || gig.substitute_not_paid === true);
  const [substitutePaid, setSubstitutePaid] = useState(gig.substitute_paid === 1 || gig.substitute_paid === true);
  const [updating, setUpdating] = useState(false);
  const [showPaidAmountEdit, setShowPaidAmountEdit] = useState(false);
  const [paidAmountInput, setPaidAmountInput] = useState('');
  
  // Check if this is a substitution gig
  const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
  const isSubForMe = gigType === 'sub_for_me';
  const today = moment().startOf('day');
  const isUpcoming = gig.start_date && moment(gig.start_date).startOf('day').isAfter(today);

  const startDate = moment(gig.start_date);
  const endDate = moment(gig.end_date);
  const days = endDate.diff(startDate, 'days') + 1;
  const rateType = gig.rate_type || 'daily';
  const baseRevenue = rateType === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
  const totalAdditionalCharges = gig.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
  const totalRevenue = baseRevenue + totalAdditionalCharges;
  const totalExpenses = gig.expenses?.reduce((sum, exp) => sum + parseFloat(exp.amount), 0) || 0;
  const selfExpenses = gig.expenses?.filter(exp => exp.covered_by === 'self').reduce((sum, exp) => sum + parseFloat(exp.amount), 0) || 0;
  const companyExpenses = gig.expenses?.filter(exp => exp.covered_by === 'company').reduce((sum, exp) => sum + parseFloat(exp.amount), 0) || 0;
  
  // Calculate substitute payment if applicable
  const substitutePayment = (gigType === 'sub_for_me' && gig.substitute_amount) ? parseFloat(gig.substitute_amount) : 0;
  
  // Calculate paid amount and payment status
  const paidAmount = gig.paid_amount !== undefined && gig.paid_amount !== null ? parseFloat(gig.paid_amount) : 0;
  // isFullyPaid requires BOTH the paid flag AND the amount to match (or exceed) totalRevenue
  const isFullyPaid = isPaid && paidAmount >= totalRevenue && totalRevenue > 0;
  const isPartiallyPaid = !isPaid && paidAmount > 0 && paidAmount < totalRevenue;
  const missingAmount = totalRevenue - paidAmount;
  const paidPercentage = totalRevenue > 0 ? (paidAmount / totalRevenue) * 100 : 0;
  const missingPercentage = totalRevenue > 0 ? (missingAmount / totalRevenue) * 100 : 0;
  
  // Net profit = Revenue - Self Expenses - Substitute Payment
  const netProfit = totalRevenue - selfExpenses - substitutePayment;

  // Update state when gig prop changes (but only if not currently updating)
  // This prevents state resets during active updates
  useEffect(() => {
    if (!updating && !showPaidAmountEdit) {
      const newCanceled = gig.is_canceled === 1 || gig.is_canceled === true;
      const newPaid = gig.paid === 1 || gig.paid === true;
      const newSubstituteNotPaid = gig.substitute_not_paid === 1 || gig.substitute_not_paid === true;
      const newSubstitutePaid = gig.substitute_paid === 1 || gig.substitute_paid === true;
      setIsCanceled(newCanceled);
      setIsPaid(newPaid);
      setSubstituteNotPaid(newSubstituteNotPaid);
      setSubstitutePaid(newSubstitutePaid);
      setPaidAmountInput(gig.paid_amount !== undefined && gig.paid_amount !== null ? String(gig.paid_amount) : '');
    }
  }, [gig.id, gig.is_canceled, gig.paid, gig.substitute_not_paid, gig.substitute_paid, gig.paid_amount, updating, showPaidAmountEdit]); // Sync when gig ID changes or when status fields change (but only if not updating and not editing paid amount)

  const handleCanceledChange = async (e) => {
    const checked = e.target ? e.target.checked : (e.checked !== undefined ? e.checked : !isCanceled);
    
    if (updating) {
      return;
    }
    
    setUpdating(true);
    setIsCanceled(checked); // Update immediately
    
    try {
      const updatedGig = {
        title: gig.title,
        client_id: gig.client_id || null,
        venue: gig.venue || null,
        start_date: gig.start_date,
        end_date: gig.end_date,
        daily_rate: gig.daily_rate,
        rate_type: gig.rate_type || 'daily',
        paid: gig.paid === 1 || gig.paid === true,
        paid_amount: gig.paid_amount !== undefined && gig.paid_amount !== null ? gig.paid_amount : null,
        is_substitution: gig.is_substitution === 1 || gig.is_substitution === true,
        gig_type: gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine'),
        substitute_name: gig.substitute_name || null,
        substitute_amount: gig.substitute_amount || null,
        subbing_for: gig.subbing_for || null,
        notes: gig.notes || null,
        is_canceled: checked,
        canceled_paid: false
      };
      
      const response = await axios.put(`${API_BASE}/gigs/${gig.id}`, updatedGig);
      const serverCanceled = response.data.is_canceled === 1 || response.data.is_canceled === true;
      
      setIsCanceled(serverCanceled);
      
      if (onGigUpdate) {
        await onGigUpdate(response.data);
      }
    } catch (error) {
      console.error('Error updating canceled status:', error);
      setIsCanceled(!checked); // Revert on error
      alert(`Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handlePaidChange = async (checked) => {
    setUpdating(true);
    setIsPaid(checked); // Update immediately for responsive UI
    try {
      let newPaidAmount;
      const currentPaidAmount = gig.paid_amount !== undefined && gig.paid_amount !== null ? parseFloat(gig.paid_amount) : 0;
      
      if (checked) {
        // When marking as paid, set paid_amount to totalRevenue
        newPaidAmount = totalRevenue;
      } else {
        // When marking as unpaid, preserve the existing paid_amount (don't reset to 0)
        newPaidAmount = currentPaidAmount;
      }
      
      const updatedGig = {
        title: gig.title,
        client_id: gig.client_id || null,
        venue: gig.venue || null,
        start_date: gig.start_date,
        end_date: gig.end_date,
        daily_rate: gig.daily_rate,
        rate_type: gig.rate_type || 'daily',
        paid: checked,
        paid_amount: newPaidAmount,
        is_substitution: gig.is_substitution === 1 || gig.is_substitution === true,
        gig_type: gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine'),
        substitute_name: gig.substitute_name || null,
        substitute_amount: gig.substitute_amount || null,
        subbing_for: gig.subbing_for || null,
        notes: gig.notes || null,
        is_canceled: gig.is_canceled === 1 || gig.is_canceled === true,
        canceled_paid: gig.canceled_paid === 1 || gig.canceled_paid === true,
        substitute_not_paid: gig.substitute_not_paid === 1 || gig.substitute_not_paid === true,
        substitute_paid: gig.substitute_paid === 1 || gig.substitute_paid === true
      };
      const response = await axios.put(`${API_BASE}/gigs/${gig.id}`, updatedGig);
      const serverPaid = response.data.paid === 1 || response.data.paid === true;
      setIsPaid(serverPaid); // Update from server response
      if (onGigUpdate) {
        // Preserve expenses and additional_charges from the current gig if the response doesn't include them
        let gigWithData = response.data;
        // Ensure paid field is correctly set from server response
        gigWithData.paid = serverPaid ? 1 : 0;
        if (gig.expenses && (!response.data.expenses || response.data.expenses.length === 0)) {
          gigWithData = { ...gigWithData, expenses: gig.expenses };
        }
        if (gig.additional_charges && (!response.data.additional_charges || response.data.additional_charges.length === 0)) {
          gigWithData = { ...gigWithData, additional_charges: gig.additional_charges };
        }
        await onGigUpdate(gigWithData);
        // Sync state after onGigUpdate completes to ensure we have the latest data
        setIsPaid(gigWithData.paid === 1 || gigWithData.paid === true);
      }
    } catch (error) {
      console.error('Error updating paid status:', error);
      setIsPaid(!checked); // Revert on error
      alert('Error updating paid status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handlePaidAmountSave = async () => {
    const newPaidAmount = paidAmountInput === '' || paidAmountInput === '0' ? null : parseFloat(paidAmountInput);
    
    if (newPaidAmount !== null && (isNaN(newPaidAmount) || newPaidAmount < 0)) {
      alert('Please enter a valid amount (0 or greater)');
      return;
    }

    setUpdating(true);
    setShowPaidAmountEdit(false);
    
    try {
      const updatedGig = {
        title: gig.title,
        client_id: gig.client_id || null,
        venue: gig.venue || null,
        start_date: gig.start_date,
        end_date: gig.end_date,
        daily_rate: gig.daily_rate,
        rate_type: gig.rate_type || 'daily',
        paid: gig.paid === 1 || gig.paid === true,
        paid_amount: newPaidAmount,
        is_substitution: gig.is_substitution === 1 || gig.is_substitution === true,
        gig_type: gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine'),
        substitute_name: gig.substitute_name || null,
        substitute_amount: gig.substitute_amount || null,
        subbing_for: gig.subbing_for || null,
        notes: gig.notes || null,
        is_canceled: gig.is_canceled === 1 || gig.is_canceled === true,
        canceled_paid: gig.canceled_paid === 1 || gig.canceled_paid === true,
        substitute_not_paid: gig.substitute_not_paid === 1 || gig.substitute_not_paid === true,
        substitute_paid: gig.substitute_paid === 1 || gig.substitute_paid === true
      };
      
      const response = await axios.put(`${API_BASE}/gigs/${gig.id}`, updatedGig);
      
      if (onGigUpdate) {
        const gigWithExpenses = gig.expenses && (!response.data.expenses || response.data.expenses.length === 0)
          ? { ...response.data, expenses: gig.expenses }
          : response.data;
        await onGigUpdate(gigWithExpenses);
      }
    } catch (error) {
      console.error('Error updating paid amount:', error);
      alert(`Error updating paid amount: ${error.response?.data?.error || error.message}`);
      setShowPaidAmountEdit(true); // Re-open the edit dialog on error
    } finally {
      setUpdating(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API_BASE}/gigs/${gig.id}/pdf`, {
        responseType: 'blob',
      });
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gig-${gig.id}-${gig.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF. Please try again.');
    }
  };

  return (
    <div className="gig-details" style={{ position: 'relative', overflow: 'hidden' }}>
      {isCanceled && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-35deg)',
          fontSize: '6rem',
          fontWeight: 'bold',
          color: 'rgba(220, 53, 69, 0.15)',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 1,
          userSelect: 'none',
          letterSpacing: '0.2em'
        }}>
          CANCELED
        </div>
      )}
      <div className="gig-header">
        <div className="gig-header-top">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h2>{gig.title}</h2>
            {isUpcoming && (
              <span className="status-badge upcoming-status">
                Upcoming
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="btn btn-secondary gig-edit-btn-desktop" onClick={handleExportPDF} title="Export PDF">
              📄 Export PDF
            </button>
            <button className="btn btn-primary gig-edit-btn-desktop" onClick={() => onEdit(gig)}>
              Edit
            </button>
          </div>
        </div>
        {isSubForMe && gig.substitute_name && (
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                const newSubstitutePaid = !substitutePaid;
                setSubstitutePaid(newSubstitutePaid);
                setUpdating(true);
                try {
                  const updatedGig = {
                    title: gig.title,
                    client_id: gig.client_id || null,
                    venue: gig.venue || null,
                    start_date: gig.start_date,
                    end_date: gig.end_date,
                    daily_rate: gig.daily_rate,
                    rate_type: gig.rate_type || 'daily',
                    paid: gig.paid === 1 || gig.paid === true,
                    paid_amount: gig.paid_amount !== undefined && gig.paid_amount !== null ? gig.paid_amount : null,
                    is_substitution: gig.is_substitution === 1 || gig.is_substitution === true,
                    gig_type: gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine'),
                    substitute_name: gig.substitute_name || null,
                    substitute_amount: gig.substitute_amount || null,
                    subbing_for: gig.subbing_for || null,
                    notes: gig.notes || null,
                    is_canceled: gig.is_canceled === 1 || gig.is_canceled === true,
                    canceled_paid: gig.canceled_paid === 1 || gig.canceled_paid === true,
                    substitute_not_paid: gig.substitute_not_paid === 1 || gig.substitute_not_paid === true,
                    substitute_paid: newSubstitutePaid
                  };
                  const response = await axios.put(`${API_BASE}/gigs/${gig.id}`, updatedGig);
                  if (onGigUpdate) {
                    await onGigUpdate(response.data);
                  }
                  setSubstitutePaid(response.data.substitute_paid === 1 || response.data.substitute_paid === true);
                } catch (error) {
                  console.error('Error updating substitute paid status:', error);
                  setSubstitutePaid(!newSubstitutePaid);
                  alert(`Error: ${error.response?.data?.error || error.message}`);
                } finally {
                  setUpdating(false);
                }
              }}
              className={`btn ${substitutePaid ? 'btn-success' : 'btn-secondary'}`}
              disabled={updating}
            >
              {substitutePaid ? 'Substitute Paid' : 'Mark Substitute as Paid'}
            </button>
            {isCanceled && (
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const newSubstituteNotPaid = !substituteNotPaid;
                  setSubstituteNotPaid(newSubstituteNotPaid);
                  setUpdating(true);
                  try {
                    const updatedGig = {
                      title: gig.title,
                      client_id: gig.client_id || null,
                      venue: gig.venue || null,
                      start_date: gig.start_date,
                      end_date: gig.end_date,
                      daily_rate: gig.daily_rate,
                      rate_type: gig.rate_type || 'daily',
                      paid: gig.paid === 1 || gig.paid === true,
                      paid_amount: gig.paid_amount !== undefined && gig.paid_amount !== null ? gig.paid_amount : null,
                      is_substitution: gig.is_substitution === 1 || gig.is_substitution === true,
                      gig_type: gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine'),
                      substitute_name: gig.substitute_name || null,
                      substitute_amount: gig.substitute_amount || null,
                      subbing_for: gig.subbing_for || null,
                      notes: gig.notes || null,
                      is_canceled: gig.is_canceled === 1 || gig.is_canceled === true,
                      canceled_paid: gig.canceled_paid === 1 || gig.canceled_paid === true,
                      substitute_not_paid: newSubstituteNotPaid,
                      substitute_paid: gig.substitute_paid === 1 || gig.substitute_paid === true
                    };
                    const response = await axios.put(`${API_BASE}/gigs/${gig.id}`, updatedGig);
                    if (onGigUpdate) {
                      await onGigUpdate(response.data);
                    }
                    // Update local state from response
                    setSubstituteNotPaid(response.data.substitute_not_paid === 1 || response.data.substitute_not_paid === true);
                  } catch (error) {
                    console.error('Error updating substitute not paid status:', error);
                    setSubstituteNotPaid(!newSubstituteNotPaid);
                    alert(`Error: ${error.response?.data?.error || error.message}`);
                  } finally {
                    setUpdating(false);
                  }
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: substituteNotPaid ? '#dc3545' : '#444',
                  color: '#fff',
                  border: '1px solid #666',
                  borderRadius: '4px',
                  cursor: updating ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s'
                }}
                disabled={updating}
              >
                {substituteNotPaid ? 'Sub Won\'t Be Paid' : 'Mark Sub as Not Paid'}
              </button>
            )}
          </div>
        )}
        <div className="btn-group">
          <button 
            className={`btn ${isPaid ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => handlePaidChange(!isPaid)}
            disabled={updating}
          >
            {isPaid ? 'Paid' : 'Mark as Paid'}
          </button>
          <button className="btn btn-secondary gig-edit-btn-mobile" onClick={handleExportPDF} title="Export PDF">
            📄 PDF
          </button>
          <button className="btn btn-primary gig-edit-btn-mobile" onClick={() => onEdit(gig)}>
            Edit
          </button>
          <button
            type="button"
            className={`btn canceled-btn-desktop ${isCanceled ? 'btn-danger' : 'btn-secondary'}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleCanceledChange({ target: { checked: !isCanceled } });
            }}
            disabled={updating}
          >
            {isCanceled ? 'Canceled' : 'Mark as Canceled'}
          </button>
          <button className="btn btn-danger" onClick={() => onDelete(gig.id)}>
            Delete
          </button>
        </div>
      </div>

      <div className="gig-meta">
        {gig.client_name && (
          <div className="meta-item">
            <span className="meta-label">Client:</span>
            <span className="meta-value">
              {gig.client_name} {gig.client_company ? `(${gig.client_company})` : ''}
              <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                VAT ID: {gig.client_vat_id || 'not set yet'}
              </div>
            </span>
          </div>
        )}
        {gig.venue && (
          <div className="meta-item">
            <span className="meta-label">Venue:</span>
            <span className="meta-value">{gig.venue}</span>
          </div>
        )}
        <div className="meta-item">
          <span className="meta-label">{days === 1 ? 'Date' : 'Start Date'}:</span>
          <span className="meta-value">{startDate.format('MMM D, YYYY')}</span>
        </div>
        {days > 1 && (
          <>
            <div className="meta-item">
              <span className="meta-label">End Date:</span>
              <span className="meta-value">{endDate.format('MMM D, YYYY')}</span>
            </div>
            {rateType === 'daily' && (
              <div className="meta-item">
                <span className="meta-label">Duration:</span>
                <span className="meta-value">{days} days</span>
              </div>
            )}
          </>
        )}
        <div className="meta-item">
          <span className="meta-label">{rateType === 'flat' ? 'Flat Rate' : 'Daily Rate'}:</span>
          <span className="meta-value">€{parseFloat(gig.daily_rate).toFixed(2)}</span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Gig Type:</span>
          <span className={`meta-value ${getGigTypeClass(gig)}`}>
            {getGigTypeLabel(gig)}
          </span>
        </div>
        {(gig.gig_type === 'sub_for_other' || !!gig.is_substitution) && !!gig.subbing_for && (
          <div className="meta-item">
            <span className="meta-label">Subbing For:</span>
            <span className="meta-value" style={{ color: '#2563eb' }}>{gig.subbing_for}</span>
          </div>
        )}
        {gig.gig_type === 'sub_for_me' && (() => {
          // Check if there's a 'sub' charge with a name, otherwise use gig.substitute_name
          const subCharge = gig.additional_charges?.find(c => c.charge_type === 'sub');
          const displayName = subCharge?.substitute_name || gig.substitute_name;
          return displayName ? (
            <div className="meta-item">
              <span className="meta-label">Substitute:</span>
              <span className="meta-value">{displayName}</span>
            </div>
          ) : null;
        })()}
        {gig.gig_type === 'sub_for_me' && gig.substitute_amount > 0 && (
          <div className="meta-item">
            <span className="meta-label">Amount to Pay:</span>
            <span className="meta-value amount">€{parseFloat(gig.substitute_amount).toFixed(2)}</span>
          </div>
        )}
        <div className="meta-item">
          <span className="meta-label">Payment Status:</span>
          <span className="meta-value" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {isFullyPaid && (
              <span className="paid-status">✅ Paid</span>
            )}
            {isPartiallyPaid && (
              <>
                <span className="status-badge partial-payment-status">⚠️ Partially Paid</span>
                <div style={{ fontSize: '0.85rem', color: '#ff9800', marginTop: '0.25rem' }}>
                  Paid: €{paidAmount.toFixed(2)} ({paidPercentage.toFixed(1)}%)
                  <br />
                  Missing: €{missingAmount.toFixed(2)} ({(100 - paidPercentage).toFixed(1)}%)
                </div>
              </>
            )}
            {!isFullyPaid && !isPartiallyPaid && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <span className="unpaid-status">❌ Unpaid</span>
                <div style={{ fontSize: '0.85rem', color: '#ff6b6b', marginTop: '0.25rem' }}>
                  Missing: €{missingAmount.toFixed(2)} ({missingPercentage.toFixed(1)}%)
                </div>
              </div>
            )}
          </span>
        </div>
        {!isFullyPaid && (
          <div className="meta-item">
            <span className="meta-label">Amount Paid:</span>
            <span className="meta-value amount" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {paidAmount > 0 ? `€${paidAmount.toFixed(2)}` : '€0.00'}
              {!showPaidAmountEdit ? (
                <button 
                  className="btn btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setPaidAmountInput(gig.paid_amount !== undefined && gig.paid_amount !== null ? String(gig.paid_amount) : '');
                    setShowPaidAmountEdit(true);
                  }}
                  disabled={updating}
                  style={{ fontSize: '0.85rem', padding: '0.35rem 0.7rem' }}
                >
                  Edit
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="number"
                    value={paidAmountInput}
                    onChange={(e) => setPaidAmountInput(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={{
                      padding: '0.35rem',
                      border: '2px solid #555',
                      borderRadius: '4px',
                      backgroundColor: '#1a1a1a',
                      color: '#e5e5e5',
                      width: '100px',
                      fontSize: '0.85rem'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handlePaidAmountSave();
                      } else if (e.key === 'Escape') {
                        setShowPaidAmountEdit(false);
                        setPaidAmountInput(gig.paid_amount !== undefined && gig.paid_amount !== null ? String(gig.paid_amount) : '');
                      }
                    }}
                    autoFocus
                  />
                  <button 
                    className="btn btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handlePaidAmountSave();
                    }}
                    disabled={updating}
                    style={{ fontSize: '0.85rem', padding: '0.35rem 0.7rem' }}
                  >
                    Save
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPaidAmountEdit(false);
                      setPaidAmountInput(gig.paid_amount !== undefined && gig.paid_amount !== null ? String(gig.paid_amount) : '');
                    }}
                    disabled={updating}
                    style={{ fontSize: '0.85rem', padding: '0.35rem 0.7rem' }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </span>
          </div>
        )}
        <div className="meta-item">
          <span className="meta-label">Base Revenue:</span>
          <span className="meta-value amount">€{baseRevenue.toFixed(2)}</span>
        </div>
        {totalAdditionalCharges > 0 && (
          <div className="meta-item">
            <span className="meta-label">Additional Charges:</span>
            <span className="meta-value amount" style={{ color: '#4caf50' }}>€{totalAdditionalCharges.toFixed(2)}</span>
          </div>
        )}
        <div className="meta-item" style={{ borderTop: '1px solid #444', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
          <span className="meta-label">Total Revenue:</span>
          <span className="meta-value amount" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>€{totalRevenue.toFixed(2)}</span>
        </div>
      </div>

      {gig.notes && (
        <div className="gig-notes">
          <h3>Notes</h3>
          <p>{gig.notes}</p>
        </div>
      )}

      <div className="expenses-section">
        <div className="expenses-header">
          <h3>Additional Charges</h3>
          <button className="btn btn-primary" onClick={onAddAdditionalCharge}>
            + Add Charge
          </button>
        </div>

        {gig.additional_charges && gig.additional_charges.length > 0 ? (
          <>
            <ul className="expenses-list">
              {gig.additional_charges.map(charge => (
                <li key={charge.id} className="expense-item">
                  <div className="expense-info">
                    <div className="expense-description">
                      <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{charge.charge_type}</span>
                      {charge.notes && (
                        <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                          {charge.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="expense-actions">
                    <span className="expense-amount">€{parseFloat(charge.amount).toFixed(2)}</span>
                    <button
                      className="btn-icon"
                      onClick={() => onEditAdditionalCharge(charge)}
                      title="Edit charge"
                    >
                      Edit
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => onAdditionalChargeDelete(charge.id)}
                      title="Delete charge"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="total-section">
              <span className="total-label">Total Additional Charges:</span>
              <span className="total-amount" style={{ color: '#4caf50' }}>€{totalAdditionalCharges.toFixed(2)}</span>
            </div>
          </>
        ) : (
          <p style={{ color: '#999', marginTop: '1rem' }}>No additional charges added yet.</p>
        )}
      </div>

      <div className="expenses-section">
        <div className="expenses-header">
          <h3>Expenses</h3>
          <button className="btn btn-primary" onClick={onAddExpense}>
            + Add Expense
          </button>
        </div>

        {gig.expenses && gig.expenses.length > 0 ? (
          <>
            <ul className="expenses-list">
              {gig.expenses.map(expense => (
                <li key={expense.id} className="expense-item">
                  <div className="expense-info">
                    <div className="expense-description">
                      {expense.description}
                      <span className={`expense-covered-badge ${expense.covered_by === 'company' ? 'company-covered' : 'self-covered'}`}>
                        {expense.covered_by === 'company' ? '💰 Company' : '👤 Self'}
                      </span>
                    </div>
                    <div className="expense-date">
                      {moment(expense.expense_date).format('MMM D, YYYY')}
                    </div>
                  </div>
                  <div className="expense-actions">
                    <span className="expense-amount">€{parseFloat(expense.amount).toFixed(2)}</span>
                    <button
                      className="btn-icon"
                      onClick={() => onEditExpense(expense)}
                      title="Edit expense"
                    >
                      Edit
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => onExpenseDelete(expense.id)}
                      title="Delete expense"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="total-section">
              <span className="total-label">Total Expenses:</span>
              <span className="total-amount">€{totalExpenses.toFixed(2)}</span>
            </div>
            <div className="total-section">
              <span className="total-label">Self-Covered Expenses:</span>
              <span className="total-amount">€{selfExpenses.toFixed(2)}</span>
            </div>
            <div className="total-section">
              <span className="total-label">Company-Covered Expenses:</span>
              <span className="total-amount" style={{ color: '#4caf50' }}>€{companyExpenses.toFixed(2)}</span>
            </div>
            {substitutePayment > 0 && (
              <div className="total-section">
                <span className="total-label">Substitute Payment:</span>
                <span className="total-amount" style={{ color: '#ff6b6b' }}>€{substitutePayment.toFixed(2)}</span>
              </div>
            )}
            <div className="total-section" style={{ borderTop: '2px solid #444', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
              <span className="total-label">Net Profit{substitutePayment > 0 ? ' (After Substitute Payment)' : ''}:</span>
              <span className="total-amount" style={{ color: netProfit >= 0 ? '#ffd700' : '#ff6b6b', fontSize: '1.2rem', fontWeight: 'bold' }}>
                €{netProfit.toFixed(2)}
              </span>
            </div>
          </>
        ) : (
          <p style={{ color: '#999', marginTop: '1rem' }}>No expenses added yet.</p>
        )}

        {/* Show substitute payment and profit summary even if no expenses */}
        {(!gig.expenses || gig.expenses.length === 0) && substitutePayment > 0 && (
          <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '2px solid #444' }}>
            <div className="total-section">
              <span className="total-label">Substitute Payment:</span>
              <span className="total-amount" style={{ color: '#ff6b6b' }}>€{substitutePayment.toFixed(2)}</span>
            </div>
            <div className="total-section" style={{ borderTop: '2px solid #444', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
              <span className="total-label">Net Profit (After Substitute Payment):</span>
              <span className="total-amount" style={{ color: netProfit >= 0 ? '#ffd700' : '#ff6b6b', fontSize: '1.2rem', fontWeight: 'bold' }}>
                €{netProfit.toFixed(2)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GigDetails;

